import { timingOnMs, type TimingTendency } from '../melody/score-timing.js'

/**
 * Melody Trainer v1 - the session shape, pure.
 *
 * The sibling of `pitch-training.ts`'s session fold, with one thing added: a session's timing
 * tendency, which is a different question from its pitch score and is the reason a player comes
 * back to this tool rather than the Pitch Trainer.
 *
 * Nothing here knows about audio. Everything an attempt needs to be *performed* - transposition,
 * audition, capture, scoring - happens above this module.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6
 */

/** The three input modes of §6's table. */
export const melodyTrainerModes = ['voice', 'instrument', 'midi'] as const

export type MelodyTrainerMode = (typeof melodyTrainerModes)[number]

export interface MelodyTrainerAttempt {
  /** `absolute.mean` for the attempt, or null when nothing was heard. */
  meanScore: number | null
  /** Signed mean onset error in ms, or null when nothing was paired. */
  meanErrorMs: number | null
  tendency: TimingTendency | null
}

export interface MelodyTrainerStats {
  /** Attempts that produced a score. An attempt nobody performed is not a zero. */
  scored: number
  total: number
  /** Mean of the scored attempts' pitch scores, 0-100. */
  meanPercent: number
  /** Mean of the per-attempt mean errors. Null when no attempt produced one. */
  meanErrorMs: number | null
  /** What the session as a whole leaned. Null when there was nothing to measure a lean from. */
  tendency: TimingTendency | null
}

const isNumber = (value: number | null): value is number => value !== null

export const computeMelodyTrainerStats = (attempts: MelodyTrainerAttempt[]): MelodyTrainerStats => {
  const scores = attempts.map(attempt => attempt.meanScore).filter(isNumber)
  const errors = attempts.map(attempt => attempt.meanErrorMs).filter(isNumber)

  const meanPercent =
    scores.length === 0 ? 0 : Math.round((scores.reduce((total, score) => total + score, 0) / scores.length) * 100)

  const meanErrorMs =
    errors.length === 0 ? null : errors.reduce((total, error) => total + error, 0) / errors.length

  /*
   * The even band is `timingOnMs`, the same constant the per-attempt verdict uses. A second
   * threshold here could tell a player their session was even on a screen that had just told them
   * every attempt was rushing.
   */
  const tendency: TimingTendency | null =
    meanErrorMs === null ? null : Math.abs(meanErrorMs) <= timingOnMs ? 'even' : meanErrorMs < 0 ? 'rushing' : 'dragging'

  return { scored: scores.length, total: attempts.length, meanPercent, meanErrorMs, tendency }
}

/** Parse the `?mode=` query parameter. Anything unrecognised is null - the welcome screen asks. */
export const parseMelodyTrainerMode = (raw: string | null): MelodyTrainerMode | null =>
  melodyTrainerModes.find(mode => mode === raw) ?? null
